import { MNSproCloudApi } from '../..';
import {
  ChatLockResponse,
  ChatThreadType,
  ChatUserType,
  CreateChatLockRequest,
  CreateChatParticipantRequest,
  GuardianChatCreateThreadRequestContent,
  GuardianChatParticipantCandidatesResponse,
  GuardianChatSendChatMessageRequestContent,
  GuardianChatThreadItemResponse,
} from '../api/v2';

export class GuardianChatService {
  private static getApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.GuardianChatApiFactory(config);
    return api;
  }

  static async getAcsToken(authToken: string, organizationId: string) {
    const api = GuardianChatService.getApi(authToken);
    const response = await api.getToken(organizationId);

    return response.data;
  }

  static async refreshAcsToken(authToken: string, organizationId: string) {
    const api = GuardianChatService.getApi(authToken);
    const response = await api.refreshToken(organizationId);
    return response.data;
  }

  static async createThread(
    authToken: string,
    organizationId: string,
    createRequest: GuardianChatCreateThreadRequestContent,
  ) {
    const api = GuardianChatService.getApi(authToken);
    const response = await api.createChatThread(organizationId, createRequest);

    return response.data;
  }

  static async getMyThreads(
    authToken: string,
    organizationId: string,
  ): Promise<GuardianChatThreadItemResponse[]> {
    const api = GuardianChatService.getApi(authToken);
    const response = await api.getChatThreadsOfMe(organizationId);

    return response.data;
  }

  static async getThread(authToken: string, organizationId: string, threadId: string) {
    const api = GuardianChatService.getApi(authToken);
    const response = await api.getChatThreadMessages(organizationId, threadId);
    return response.data;
  }

  static async sendMessage(
    authToken: string,
    organizationId: string,
    threadId: string,
    message: string,
    clientMessageId: string,
    fileIds?: string[],
  ) {
    const api = GuardianChatService.getApi(authToken);
    const content: GuardianChatSendChatMessageRequestContent = {
      message,
      clientMessageId,
      fileIds,
    };
    const response = await api.sendChatMessage(organizationId, threadId, content);
    return response.data;
  }

  static async getParticipantCandidates(
    authToken: string,
    organizationId: string,
    threadId?: string,
  ) {
    const api = GuardianChatService.getApi(authToken);
    const response = await api.getParticipantCandidates(organizationId, threadId);
    return response.data;
  }

  static async createChatThread(
    authToken: string,
    organizationId: string,
    topic: string,
    threadType: ChatThreadType,
    participants: CreateChatParticipantRequest[],
    wardId?: string,
  ) {
    const api = GuardianChatService.getApi(authToken);
    const config: GuardianChatCreateThreadRequestContent = {
      topic,
      threadType,
      wardId,
      participants,
    };

    const response = await api.createChatThread(organizationId, config);

    return response.data;
  }

  static async addChatParticipants(
    authToken: string,
    organizationId: string,
    threadId: string,
    candidates: GuardianChatParticipantCandidatesResponse[],
  ) {
    const createRequests: CreateChatParticipantRequest[] = candidates.map((p) => ({
      userId: p.userId,
      // @todo remove typecast because of "duplicate2" type
      userType: p.userType as unknown as ChatUserType,
    }));

    const api = GuardianChatService.getApi(authToken);
    const response = await api.addChatParticipants(organizationId, threadId, createRequests);

    return response.data;
  }

  static async removeChatParticipant(
    authToken: string,
    organizationId: string,
    threadId: string,
    participantId: string,
  ) {
    const api = GuardianChatService.getApi(authToken);
    await api.removeChatParticipant(organizationId, threadId, participantId);
  }

  static async deleteChat(authToken: string, organizationId: string, threadId: string) {
    const api = GuardianChatService.getApi(authToken);
    await api.deleteChatThread(organizationId, threadId);
  }

  static async updateChatConfiguration(
    authToken: string,
    organizationId: string,
    threadId: string,
    createChatLockRequest: CreateChatLockRequest,
  ): Promise<ChatLockResponse> {
    const api = GuardianChatService.getApi(authToken);
    const response = await api.updateChatConfiguration(
      organizationId,
      threadId,
      createChatLockRequest,
    );

    return response.data;
  }

  static async uploadFile(
    authToken: string,
    organizationId: string,
    threadId: string,
    file: File,
  ): Promise<string> {
    const api = GuardianChatService.getApi(authToken);
    const response = await api.uploadFile(organizationId, threadId, file);

    return response.data;
  }

  static async getFileDownloadUrl(
    authToken: string,
    organizationId: string,
    threadId: string,
    fileId: string,
  ) {
    const api = GuardianChatService.getApi(authToken);
    const response = await api.getFileDownloadUrl(organizationId, threadId, fileId);

    return response.data;
  }

  static async markRead(authToken: string, organizationId: string, threadId: string) {
    const api = GuardianChatService.getApi(authToken);
    await api.markThreadAsRead(organizationId, threadId);
  }
}
